import type { Lobby, LobbyMember } from '@/lib/types'
import type { TeamFeature } from '@/lib/features'

/**
 * The rules of a lobby that the browser decides (migrations/0061_lobbies.sql
 * holds the ones it must not): which lobby, if any, to put in front of someone,
 * what the popup says, and how a scoreboard sorts.
 *
 * Pure, so `scripts/lobby-check.mjs` can assert it without a browser — same
 * arrangement as lib/missile-game.ts.
 */

/** The cap 0061's check constraint allows. */
export const LOBBY_MAX = 5

/**
 * How long an open lobby keeps inviting people. Matches the sweep in
 * `app.open_lobby`: a lobby whose host walked away should stop interrupting
 * the team long before anybody opens the next one.
 */
export const LOBBY_STALE_MS = 30 * 60 * 1000

/**
 * What a lobby is for. The database only knows `kind` as a string; this is
 * where a kind gets a title for the popup, a page to send people to, and the
 * team switch it lives behind. A future use of the lobby is one entry here.
 */
export interface LobbyKind {
  kind: string
  title: string
  path: (lobbyId: string) => string
  feature: TeamFeature['key']
}

export const LOBBY_KINDS: LobbyKind[] = [
  {
    kind: 'missile-defence-team',
    title: 'Missile Defence',
    path: (id) => `/games/missile-defence-team/${id}`,
    feature: 'games_enabled',
  },
]

export function lobbyKind(kind: string): LobbyKind | undefined {
  return LOBBY_KINDS.find((k) => k.kind === kind)
}

/** The popup's question, word for word as it was asked for. */
export function inviteLine(host: string | null, title: string): string {
  return `${host || 'Someone'}'s starting ${title}, want to join?`
}

export type LobbyWithMembers = Lobby & { lobby_members: Pick<LobbyMember, 'user_id'>[] }

/**
 * The lobby to invite this person to, or null.
 *
 * Not the host's own, not one they are already in, not one they said No to,
 * not one with no seat left, not one that has started (the roster is locked),
 * not one gone stale, not one whose page they are looking at, and only a kind
 * this build knows and this team has switched on.
 */
export function pickInvite(
  lobbies: LobbyWithMembers[],
  opts: {
    userId: string | undefined
    dismissed: string[]
    now: number
    pathname: string
    enabled: (kind: LobbyKind) => boolean
  },
): LobbyWithMembers | null {
  if (!opts.userId) return null
  for (const l of lobbies) {
    const kind = lobbyKind(l.kind)
    if (!kind || !opts.enabled(kind)) continue
    if (l.status !== 'open') continue
    if (l.host_id === opts.userId) continue
    if (l.lobby_members.some((m) => m.user_id === opts.userId)) continue
    if (l.lobby_members.length >= l.max_players) continue
    if (opts.dismissed.includes(l.id)) continue
    if (opts.now - new Date(l.created_at).getTime() > LOBBY_STALE_MS) continue
    if (opts.pathname === kind.path(l.id)) continue
    return l
  }
  return null
}

/**
 * Scoreboard order: the highest count first. Level counts put whoever won
 * first, then whoever is still playing (they can still climb), then whoever is
 * out; and after that whoever said yes first, so the order does not shuffle
 * between polls.
 */
export function standings<M extends Pick<LobbyMember, 'score' | 'outcome' | 'joined_at'>>(members: M[]): M[] {
  const rank = (o: LobbyMember['outcome']) => (o === 'won' ? 0 : o === null ? 1 : 2)
  return [...members].sort(
    (a, b) =>
      b.score - a.score ||
      rank(a.outcome) - rank(b.outcome) ||
      a.joined_at.localeCompare(b.joined_at),
  )
}
